import React, { useEffect, useRef, useState } from "react";
import { ButtonGroup, Card, Badge } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Column } from "primereact/column";
import { useGetHeOneQuery } from "../../app/features/apiSlice";
import Loader from "../Loader";
import Error from "../Error";
import { formatNumberToLocal } from "../../helpers";
import CurrencyValue from "../CurrencyValue";
import moment from "moment";
import { apiSlice } from "../../app/features/apiSlice";
import ExportCSVButton from "../exportcsv/ExportCsvButton";
import SearchBox from "../SearchBox";
import ReusableDataTable from "../ReusableDataTable";

const initialSorting = {
  sortBy: "id",
  sortOrder: -1,
};

const HeliorOneList = ({ pageTitle }) => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const sessionId = useRef(Date.now()).current;
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sorting, setSorting] = useState(initialSorting);

  const { data, isLoading, isSuccess, isError, error, isFetching } =
    useGetHeOneQuery({
      ...sorting,
      sessionId,
      searchQuery: searchQuery,
      page: currentPage,
      limit: 10,
    });

  const processItemForExport = (item) => {
    return {
      [t("contract_date")]: moment(item?.contract_date).format("DD-MM-YYYY"),
      [t("contract_code")]: item?.contract_code,
      [t("collaborator")]:
        `${item?.client?.orgUser?.referrer?.user?.first_name} ${item?.client?.orgUser?.referrer?.user?.last_name}`,
      [t("client")]: `${item?.client?.first_name} ${item?.client?.last_name}`,
      [t("case_duration_years")]: item?.duration,
      [t("fixings")]: item?.total_gr,
      [t("totCost")]: formatNumberToLocal(item?.total_payment) || 0,
      [t("totGuarantee")]:
        formatNumberToLocal(item?.total_payment_with_margin) || 0,
      [t("status")]: item.status ? "Active" : "Inactive",
    };
  };

  useEffect(() => {
    if (isSuccess) {
      const items = data?.data;
      const tableItems = [];
      items?.forEach((item) => {
        let clientName = "";
        if (item?.client?.orgUser?.type === "COMPANY") {
          clientName = item?.client?.orgUser.company_name;
        } else {
          clientName = `${item?.client?.first_name} ${item?.client?.last_name}`;
        }

        const collaboratorName = [
        item?.collaborator?.first_name,
        item?.collaborator?.last_name,
      ]
        .filter(Boolean)
        .join(" ");

        tableItems.push({
          id: item.id,
          status: item.status,
          contract_date: item?.contract_date
            ? moment(item?.contract_date).format("DD-MM-YYYY")
            : "",
          contract_code: item?.contract_code,
          client: clientName ? clientName.trim() : "-",
          clientId: item?.client?.id,
          collaborator: collaboratorName,
          collaboratorId: item?.collaborator?.id,
          case_duration_years: item?.duration,
          ingot_gr: item?.total_gr,
          totCost: item?.total_payment ?? 0,
          totGuarantee: item?.total_payment_with_margin ?? 0,
          currency: item?.currency,
          exchange_rate: item?.fx_rate,
          action: "",
        });
      });
      setTableData(tableItems);
    }
  }, [isSuccess, data]);

  const totalRecords = data?.pagination?.total || 0;

  return (
    <>
      {isLoading && <Loader />}
      {isError && <Error error={error} />}
      {isSuccess && (
        <Card bg="dark1 text-light">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>{pageTitle}</div>
            <div className="d-flex justify-content-between align-items-center">
              <ExportCSVButton
                apiFn={apiSlice.endpoints.getHeOne.initiate}
                processItem={processItemForExport}
                filenamePrefix="export_xone_list"
                queryParams={{
                  sessionId,
                  searchQuery,
                  ...sorting,
                }}
              />
              <SearchBox
                setCurrentPage={setCurrentPage}
                setSearchQuery={setSearchQuery}
              />
            </div>
          </Card.Header>
          <Card.Body>
            <ReusableDataTable
              tableData={tableData}
              currentPage={currentPage}
              totalRecords={totalRecords}
              isLoading={isLoading}
              isFetching={isFetching}
              sorting={sorting}
              searchQuery={searchQuery}
              setSorting={setSorting}
              setCurrentPage={setCurrentPage}
              initialSorting={initialSorting}
            >
              <Column
                sortable
                field="contract_date"
                header={t("contract_date")}
              />
              <Column
                sortable
                field="contract_code"
                header={t("contract_code")}
              />
              <Column
                // sortable
                field="collaborator"
                header={t("collaborator")}
                body={(rowData) => (
                  <Link
                    className="text-light"
                    to={`/${lang}/collaborator/${rowData.collaboratorId}`}
                  >
                    {rowData.collaborator}
                  </Link>
                )}
              />
              <Column
                sortable
                field="client"
                header={t("client")}
                body={(rowData) => (
                  <Link
                    className="text-light"
                    to={`/${lang}/client/${rowData.clientId}`}
                  >
                    {rowData.client}
                  </Link>
                )}
              />
              <Column
                sortable
                field="case_duration_years"
                header={t("case_duration_years")}
              />
              <Column
                // sortable
                field="ingot_gr"
                header={t("purchasedGrams")}
                body={(rowData) => `${rowData.ingot_gr}gr`}
              />
              <Column
                field="totCost"
                header={t("totCost")}
                body={(rowData) => (
                  <CurrencyValue amount={rowData.totCost} currency={rowData.currency} rate={rowData.exchange_rate} />
                )}
              />
              <Column
                field="totGuarantee"
                header={t("totGuarantee")}
                body={(rowData) => (
                  <CurrencyValue amount={rowData.totGuarantee} currency={rowData.currency} rate={rowData.exchange_rate} />
                )}
              />
              <Column
                sortable
                field="status"
                header={t("status")}
                body={(rowData) => (
                  <Badge
                    bg={`${
                      rowData.status === "Active" ? "success" : "secondary"
                    }`}
                  >
                    {rowData.status === "Active" ? t("active") : t("inactive")}
                  </Badge>
                )}
              ></Column>
              <Column
                body={(rowData) => (
                  <ButtonGroup size="sm">
                    <Link
                      to={`/${lang}/collaborator/excelior-one/${rowData.contract_code}`}
                      className="btn btn-outline-light"
                    >
                      <i className="fa fa-eye" />
                    </Link>
                  </ButtonGroup>
                )}
              />
            </ReusableDataTable>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default HeliorOneList;
